import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Instagram, Facebook } from "lucide-react";
import { useT } from "@/i18n";
import api from "@/lib/api";
import { fmtEventDate } from "@/lib/dates";

let _artistSlugCache = null;

const fmt = (iso, country, lang) => {
    const { primary, secondary } = fmtEventDate(iso, country, lang);
    const parts = primary.split(/[\s,]+/).filter(Boolean);
    const isUS = /^[A-Z]{3}$/.test(parts[0]);
    if (isUS) return { day: parts[1] || "", month: parts[0] || "", year: parts[2] || "", time: secondary };
    return { day: parts[0] || "", month: parts[1] || "", year: parts[2] || "", time: secondary };
};

// Event list rows in the /events "upcoming" format — visible flyer thumbnail,
// big date, venue line, tickets CTA, and a floating flyer preview on hover.
// Used by the events page AND the artist detail "appearances" section.
export const EventsListHover = ({ events }) => {
    const { t, lang } = useT();
    const containerRef = useRef(null);
    const [flyer, setFlyer] = useState(null);
    // name → slug map so lineup names deep-link to /artists/{slug} (SEO internal linking)
    const [slugMap, setSlugMap] = useState(_artistSlugCache || {});
    useEffect(() => {
        if (_artistSlugCache) return;
        api.get("/artists").then((r) => {
            const m = {};
            (r.data || []).forEach((a) => { if (a.name && a.slug) m[a.name.toLowerCase().trim()] = a.slug; });
            _artistSlugCache = m;
            setSlugMap(m);
        }).catch(() => {});
    }, []);
    const nameLink = (n, i) => {
        const slug = slugMap[(n || "").toLowerCase().trim()];
        return (
            <span key={i}>
                {i > 0 && " · "}
                {slug ? (
                    <Link to={`/artists/${slug}`} className="underline decoration-1 underline-offset-2 hover:opacity-100">{n}</Link>
                ) : n}
            </span>
        );
    };

    const handleMove = (e) => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        setFlyer((prev) => (prev ? { ...prev, x: e.clientX - rect.left, y: e.clientY - rect.top } : prev));
    };

    return (
        <div ref={containerRef} onMouseMove={handleMove} className="relative">
            <ul className="border-y border-current/10 divide-y divide-current/10">
                {events.map((e) => {
                    const d = fmt(e.date, e.country, lang);
                    const flyerImg = e.flyer_url || e.image_url;
                    return (
                        <li
                            key={e.id}
                            data-testid={`event-item-${e.id}`}
                            onMouseEnter={(evt) => {
                                if (!flyerImg || !containerRef.current) return;
                                const rect = containerRef.current.getBoundingClientRect();
                                setFlyer({ url: flyerImg, x: evt.clientX - rect.left, y: evt.clientY - rect.top });
                            }}
                            onMouseLeave={() => setFlyer(null)}
                            className="relative grid grid-cols-12 gap-4 items-center py-5 md:py-6 px-2 md:px-4 group hover:bg-[color:var(--sc-accent)] hover:text-white transition-colors duration-300"
                        >
                            {/* Visible flyer thumbnail — always shown, not just on hover */}
                            <div className="col-span-3 md:col-span-1">
                                {flyerImg ? (
                                    <div className="w-full max-w-[84px] aspect-[3/4] overflow-hidden">
                                        <img src={flyerImg} alt={e.title} loading="lazy" className="w-full h-full object-cover" data-testid={`event-thumb-${e.id}`} />
                                    </div>
                                ) : (
                                    <div className="w-full max-w-[84px] aspect-[3/4] border border-current/15" />
                                )}
                            </div>
                            <div className="col-span-3 md:col-span-2 pr-2">
                                <div className="font-heading font-black text-3xl md:text-5xl leading-none">{d.day}</div>
                                <div className="font-mono text-[10px] uppercase tracking-[0.25em] mt-1">{d.month} {d.year}</div>
                            </div>
                            <div className="col-span-6 md:col-span-5">
                                <div className="font-heading font-bold text-xl md:text-2xl uppercase tracking-tight">{e.title}</div>
                                <div className="font-mono text-[11px] md:text-xs uppercase tracking-[0.2em] opacity-60 group-hover:opacity-100 mt-1 flex items-center gap-2 flex-wrap">
                                    <MapPin size={12} />
                                    {e.venue_url ? (
                                        <a href={e.venue_url} target="_blank" rel="noreferrer" className="link-underline">{e.venue}</a>
                                    ) : (
                                        <span>{e.venue}</span>
                                    )}
                                    <span>· {e.city}{e.country && `, ${e.country}`}</span>
                                    {d.time && <><span className="mx-1">·</span> {d.time}</>}
                                </div>
                                {e.lineup?.length > 0 && (
                                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] opacity-60 group-hover:opacity-100 mt-2">
                                        w/ {e.lineup.map(nameLink)}
                                    </div>
                                )}
                            </div>
                            <div className="col-span-12 md:col-span-4 md:text-right">
                                {e.ticket_url ? (
                                    <a
                                        href={e.ticket_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        data-testid={`ticket-${e.id}`}
                                        className="inline-flex items-center bg-[color:var(--sc-accent)] group-hover:bg-black/30 text-white font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 transition-colors"
                                    >
                                        {t("tickets")}
                                    </a>
                                ) : (e.facebook_url || e.instagram_url) ? (
                                    <div className="inline-flex items-center gap-3 md:justify-end" data-testid={`event-social-${e.id}`}>
                                        {e.instagram_url && <a href={e.instagram_url} target="_blank" rel="noreferrer" aria-label="instagram" className="opacity-70 hover:opacity-100 transition-opacity"><Instagram size={16} /></a>}
                                        {e.facebook_url && <a href={e.facebook_url} target="_blank" rel="noreferrer" aria-label="facebook" className="opacity-70 hover:opacity-100 transition-opacity"><Facebook size={16} /></a>}
                                    </div>
                                ) : null}
                            </div>
                        </li>
                    );
                })}
            </ul>
            {flyer && flyer.url && (
                <div
                    data-testid="event-flyer-preview"
                    aria-hidden="true"
                    className="pointer-events-none absolute z-40 hidden md:block"
                    style={{ left: flyer.x + 24, top: flyer.y - 120, width: 220, height: 300 }}
                >
                    <img src={flyer.url} alt="" className="w-full h-full object-cover shadow-2xl" />
                </div>
            )}
        </div>
    );
};

export default EventsListHover;
